// apps/web/src/admin/useAdminStatus.ts
import { useEffect, useState } from 'react';
import { useAppStore } from '../store/useAppStore.js';
import { getValidAccessToken } from '../lib/session.js';

export type AdminStatus = 'loading' | 'admin' | 'not-admin' | 'unauthenticated';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '';

// Subscribes to the store's userId so a fresh sign-in — which goes through
// our custom /auth/v1/token flow and bypasses supabase-js's auth-state
// events — re-fires the check and flips the link on without a page reload.
// The check itself is driven by getValidAccessToken, not by userId (the
// AdminApp shell can be loaded directly via URL before it has been
// hydrated into the store).
export function useAdminStatus(): AdminStatus {
  const userId = useAppStore((s) => s.userId);
  const [status, setStatus] = useState<AdminStatus>('loading');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Don't use supabase.rpc() — it leans on supabase-js's in-memory auth
      // state, which is empty here (sign-in bypasses supabase-js). Call the
      // RPC over REST with the access_token we own, refreshing first if it
      // expired during a long absence.
      const token = await getValidAccessToken();
      if (!token) { if (!cancelled) setStatus('unauthenticated'); return; }
      try {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_my_admin_status`, {
          method: 'POST',
          headers: {
            apikey: PUBLISHABLE_KEY,
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
        });
        if (cancelled) return;
        if (!res.ok) { setStatus('not-admin'); return; }
        const body = await res.json() as boolean | null;
        setStatus(body === true ? 'admin' : 'not-admin');
      } catch (err) {
        if (!cancelled) {
          console.error('useAdminStatus: rpc error', err);
          setStatus('not-admin');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);
  return status;
}
